import type { CategoryListAPI, ProductAPI } from '~/api/Catalog';

/**
 * Rebuilds an image list down to a single entry, keeping only the fields the
 * app actually reads. Anything else the backend sends — notably `orig`, which
 * has zero consumers in `src` — is dropped by construction.
 *
 * The kept entry is the first *usable* one, not index 0: consumers filter out
 * entries carrying neither `url` nor `thumb` and fall through to the next one,
 * so keeping a blank index 0 would leave a product with no image at all.
 */
const shrinkImages = (images: ProductAPI['images']): ProductAPI['images'] => {
  const first = images?.find((image) => image.url || image.thumb);

  return first ? [{ url: first.url, thumb: first.thumb }] : images;
};

/**
 * Trims the image lists of a flat product list and of every product variant in
 * it.
 *
 * These arrays are serialized into `window.__INITIAL_STATE__`, and no listing
 * reads either of them past its first usable entry: `Product` maps `images` to
 * `url || thumb`, and `useProductCardV3` takes the first non-empty
 * `thumb`/`url` of a variant. The multi-image consumers — the PDP gallery and
 * the product modal — are fed by `ProductState`, a separate `getProductById`
 * request these payloads do not touch.
 *
 * `product.offers[].images` is deliberately left intact: that is the array the
 * cards actually render from (`currentItem.images[0]`, where `currentItem` is
 * an `Offer`), and trimming it would be a different, riskier change.
 *
 * Call from the shared request path, so SSR and the client build the store from
 * the same payload; trimming at serialization time instead would hand the
 * client something other than what the HTML was rendered from — a hydration
 * mismatch.
 */
export const shrinkProductImages = (products: ProductAPI[] | undefined) => {
  for (const product of products || []) {
    product.images = shrinkImages(product.images);

    for (const variant of product.variants || []) {
      variant.images = shrinkImages(variant.images ?? null);
    }
  }
};

/**
 * Applies {@link shrinkProductImages} to every category of a tree.
 *
 * Category-level `images` are left alone: unlike product images they are read
 * past index 0 — `Category` falls back to `images[1]`, and the hero banner
 * builder requires exactly two entries to pick a preview and its cropped
 * variant.
 */
export const shrinkCategoryTreeImages = (
  nodes: CategoryListAPI[] | undefined,
) => {
  for (const node of nodes || []) {
    shrinkProductImages(node.category.products);
    shrinkCategoryTreeImages(node.children);
  }
};
